import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { CAMERA_PRESET_DEMO_JOB_KIND } from "@/components/cameras/types"
import { jobKindLabel } from "@/components/jobs/job-elapsed-timer"
import { shouldSkipTerminalToast, terminalToastKind } from "@/components/jobs/workspace-job-toast"
import i18n from "@/i18n"
import { APP_NAME } from "@/lib/brand"
import { cacheDesktopMedia, notifyDesktopIfBackground } from "@/lib/desktop"
import { hasSketchEditWaiter } from "@/lib/sketch-edit-waiter"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { GenerationJobRow } from "@/types/workspace"

const RELOAD_WORKSPACE_ON_TERMINAL_KINDS = new Set([
  "season_structure",
  "episodes",
  "scenes",
  "shots",
  "agent_create_shots_batch",
  "characters",
  "locations",
  "props",
  "wizard_bootstrap",
  "storyboard",
  "shooting_board",
  "video",
  "video_with_reference_prep",
  "video_with_animatic_reference_prep",
  "topaz_post_production",
  "flow_video_upscale",
  "flow_video_depth",
  "assembly_export",
  "canvas_generate",
  "project_cover",
  "character_reference",
  "character_portrait",
  "character_sheet",
  "character_emotion_sheet",
  "character_scale_pose",
  "character_expression_sheet",
  "character_gait_reference",
  "character_action_beat",
  "character_voice_demo",
  "seedance_avatar_registration",
  "seedance_asset_registration",
  "location_reference",
  "prop_reference",
  "prop_front_view",
  "scale_sheet_compose",
  "prop_scale_sheet_compose",
  "mixed_scale_sheet_compose",
  "storyboard_correction",
  "byteplus_seedance_video_correction",
  "camera_preset_demo",
])

/** Animatic jobs: partial reload only — full workspace reload races with stream URL and flashes empty UI. */
const RELOAD_ANIMATICS_ON_TERMINAL_KINDS = new Set([
  "animatic_metadata",
  "animatic_web_preview",
  "animatic_segment_poster",
  "animatic_segment_reference_clip",
  "animatic_segment_export",
  "animatic_segment_shooting_board_strip",
  "animatic_markers_split",
])

/** Jobs-only partial reload: панель шота сама рефетчит данные по useReloadOnJobFinish, полный reload сбросил бы плеер. */
const RELOAD_JOBS_ON_TERMINAL_KINDS = new Set(["shot_frame_capture"])

function announceJob(kind: "success" | "warning" | "error", message: string) {
  if (kind === "success") toast.success(message)
  else if (kind === "warning") toast.warning(message)
  else toast.error(message)
  void notifyDesktopIfBackground({ title: APP_NAME, body: message })
}

type WorkspaceJobsLifecycleProps = {
  jobsContextProjectId: number | undefined
  jobsPanelProjectId: number | undefined
  onNewlyTerminal: (jobs: GenerationJobRow[]) => void
}

/**
 * Subscribes to jobs store for poll + terminal toasts/reloads.
 * Kept out of AppShell layout so progress ticks do not re-render toolbar chrome.
 */
export function WorkspaceJobsLifecycle({
  jobsContextProjectId,
  jobsPanelProjectId,
  onNewlyTerminal,
}: WorkspaceJobsLifecycleProps) {
  const jobs = useWorkspaceJobsStore((s) => s.jobs)
  const jobStatusesRef = useRef<Record<number, string>>({})

  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => j.status === "queued" || j.status === "processing")
    if (!hasActiveJobs || !jobsContextProjectId) return

    const interval = window.setInterval(() => {
      if (document.hidden) return
      router.reload({
        only: ["jobs"],
        preserveErrors: true,
        except: ["flash"],
        preserveUrl: true,
      })
    }, 10_000)

    return () => window.clearInterval(interval)
  }, [jobs, jobsContextProjectId])

  useEffect(() => {
    let needWorkspaceReload = false
    let needAnimaticsReload = false
    let needJobsReload = false
    const newlyTerminal: GenerationJobRow[] = []

    for (const j of jobs) {
      const prev = jobStatusesRef.current[j.id]
      if (prev === "queued" || prev === "processing") {
        const toastKind = terminalToastKind(j)
        const skipTerminalToast = shouldSkipTerminalToast(j, {
          hasSketchWaiter: hasSketchEditWaiter(j.id),
        })

        if (j.status === "completed") {
          // Camera demos toast from useCameraPresetsLibrary (covers company without jobs store).
          // file_to_skill: resume via jobs panel / skills UI, no ambient toast.
          // waiter-based sketch_edit owns its own toast/apply flow.
          if (j.job_kind !== CAMERA_PRESET_DEMO_JOB_KIND && j.job_kind !== "file_to_skill" && !skipTerminalToast) {
            if (j.job_kind === "animatic_bulk_segments" && (j.bulk_failed ?? 0) > 0 && j.error_message?.trim()) {
              announceJob(
                "warning",
                i18n.t("jobs:toast.completedWithErrors", {
                  kind: jobKindLabel(toastKind),
                  done: j.bulk_done ?? 0,
                  total: j.bulk_total ?? 0,
                  failed: j.bulk_failed ?? 0,
                }),
              )
            } else if (j.billing_export_id) {
              announceJob(
                "success",
                i18n.t("jobs:toast.exportReady", { label: j.subject_label || jobKindLabel(toastKind) }),
              )
            } else {
              announceJob("success", i18n.t("jobs:toast.completed", { kind: jobKindLabel(toastKind) }))
            }
          }
          if (j.cache_urls?.length) cacheDesktopMedia(j.cache_urls)
          if (RELOAD_ANIMATICS_ON_TERMINAL_KINDS.has(j.job_kind)) needAnimaticsReload = true
          else if (
            RELOAD_WORKSPACE_ON_TERMINAL_KINDS.has(j.job_kind) ||
            (j.job_kind === "sketch_edit" &&
              ((j.shot_ids?.length ?? 0) > 0 ||
                (j.character_id != null && j.character_id > 0) ||
                (j.prop_id != null && j.prop_id > 0) ||
                (j.location_id != null && j.location_id > 0)))
          )
            needWorkspaceReload = true
          else if (RELOAD_JOBS_ON_TERMINAL_KINDS.has(j.job_kind)) needJobsReload = true
          newlyTerminal.push(j)
        }
        if (j.status === "failed") {
          if (j.job_kind !== CAMERA_PRESET_DEMO_JOB_KIND && j.job_kind !== "file_to_skill" && !skipTerminalToast) {
            const err = j.error_message?.trim()
            announceJob(
              "error",
              err
                ? i18n.t("jobs:toast.failedWithMessage", { kind: jobKindLabel(toastKind), message: err })
                : i18n.t("jobs:toast.failed", { kind: jobKindLabel(toastKind) }),
            )
          }
          if (RELOAD_ANIMATICS_ON_TERMINAL_KINDS.has(j.job_kind)) needAnimaticsReload = true
          else if (
            RELOAD_WORKSPACE_ON_TERMINAL_KINDS.has(j.job_kind) ||
            (j.job_kind === "sketch_edit" &&
              ((j.shot_ids?.length ?? 0) > 0 ||
                (j.character_id != null && j.character_id > 0) ||
                (j.prop_id != null && j.prop_id > 0) ||
                (j.location_id != null && j.location_id > 0)))
          )
            needWorkspaceReload = true
          else if (RELOAD_JOBS_ON_TERMINAL_KINDS.has(j.job_kind)) needJobsReload = true
          newlyTerminal.push(j)
        }
        if (j.status === "cancelled") {
          newlyTerminal.push(j)
        }
      }
    }

    if (newlyTerminal.length > 0) {
      onNewlyTerminal(newlyTerminal)
    }

    jobStatusesRef.current = Object.fromEntries(jobs.map((j) => [j.id, j.status]))

    if (needWorkspaceReload) {
      router.reload({ preserveErrors: true, preserveUrl: true })
    } else if (needAnimaticsReload) {
      router.reload({
        only: ["animatics_by_episode", "jobs"],
        preserveErrors: true,
        preserveUrl: true,
        except: ["flash"],
      })
    } else if (needJobsReload) {
      router.reload({
        only: ["jobs"],
        preserveErrors: true,
        preserveUrl: true,
        except: ["flash"],
      })
    }
  }, [jobs, jobsPanelProjectId, onNewlyTerminal])

  return null
}
